import NotFoundState from '@rio-cloud/rio-uikit/NotFoundState';
import { PropsWithChildren, ReactElement, ReactNode } from 'react';
import { useAppSelector } from '../../../../configuration/setup/hooks';
import { getTableViewType } from '../../reducers/viewSlice';
import { ContentLoadingRow } from './BaseTableComponents';
import { ColumnDefinition, getTableClassNames, TableSort } from './SortedTableHeader';
import { useGetUserTriggeredFetching } from '../userTriggeredFetchingHook';
import { TableHeader } from './TableHeader';
import classNames from 'classnames';

type TableProps<TableColumnEnum extends string> = PropsWithChildren<{
    columnDefinition: ColumnDefinition<TableColumnEnum>[];
    notFoundMessage: ReactElement;
    sorting: TableSort<TableColumnEnum>;
    setSorting: (sort: TableSort<TableColumnEnum>) => void;
    readonly items: readonly unknown[];
    selectionHeader?: ReactNode;
}>;

export const Table = <TableColumnEnum extends string>({
    columnDefinition,
    items,
    notFoundMessage,
    sorting,
    setSorting,
    children,
    selectionHeader,
}: TableProps<TableColumnEnum>) => {
    const viewType = useAppSelector(getTableViewType);
    const isFetchingQueryTriggerByUser = useGetUserTriggeredFetching();

    const tableClassNames = getTableClassNames(items, viewType);

    const numberOfSpecialColumns = selectionHeader ? 1 : 0;
    const numberOfColumns = numberOfSpecialColumns + columnDefinition.length;

    return (
        <table className={tableClassNames}>
            <colgroup>
                {selectionHeader && <col key="bulkSelection" className="width-50" />}
                {columnDefinition.map((def) => (
                    <col key={def.column} className={classNames(def.fixedWith && `width-${def.fixedWith}`)} />
                ))}
            </colgroup>
            <TableHeader
                columnDefinition={columnDefinition}
                sorting={sorting}
                setSorting={setSorting}
                selectionHeader={selectionHeader}
            />
            <tbody>
                {isFetchingQueryTriggerByUser ? (
                    <ContentLoadingRow numberOfColumns={numberOfColumns} />
                ) : items.length <= 0 ? (
                    <tr>
                        <td colSpan={numberOfColumns}>
                            <NotFoundState headline={notFoundMessage} outerClassName="border-none" />
                        </td>
                    </tr>
                ) : (
                    children
                )}
            </tbody>
        </table>
    );
};
